// Shared helpers for PWA install UI.
//
// Chrome/Android fires a one-shot `beforeinstallprompt` event. iOS Safari never
// does — installation there is always "Share → Add to Home Screen". Both paths
// are handled here so PwaStatus and AccountPopover stay in sync.

const INSTALL_DISMISSED_KEY = 'pwaInstallDismissedAt';
const IOS_HINT_DISMISSED_KEY = 'pwaIosHintDismissedAt';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const isRecentlyDismissed = (key) => {
  const dismissedAt = Number(localStorage.getItem(key) || 0);
  return dismissedAt > 0 && Date.now() - dismissedAt < SNOOZE_MS;
};

export const dismissInstallPrompt = () => {
  localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
};

export const dismissIosHint = () => {
  localStorage.setItem(IOS_HINT_DISMISSED_KEY, String(Date.now()));
};

export const wasInstallRecentlyDismissed = () => isRecentlyDismissed(INSTALL_DISMISSED_KEY);

export const wasIosHintRecentlyDismissed = () => isRecentlyDismissed(IOS_HINT_DISMISSED_KEY);

export const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Legacy iOS Safari signal for Add to Home Screen.
  return window.navigator.standalone === true;
};

export const isIosDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) {
    return true;
  }

  // iPadOS 13+ reports itself as MacIntel but is a touch device.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

export const getDeferredInstallPrompt = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.__deferredPwaInstall || null;
};

export const clearDeferredInstallPrompt = () => {
  if (typeof window !== 'undefined') {
    window.__deferredPwaInstall = null;
  }
};

/**
 * Opens the native Chrome/Edge install dialog when a deferred prompt exists.
 * Returns 'accepted' | 'dismissed' | 'unavailable'.
 */
export const promptPwaInstall = async () => {
  const deferred = getDeferredInstallPrompt();
  if (!deferred) {
    return 'unavailable';
  }

  deferred.prompt();
  const choice = await deferred.userChoice;
  clearDeferredInstallPrompt();
  return choice?.outcome === 'accepted' ? 'accepted' : 'dismissed';
};
